import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowRight, Check } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { FacilitySlider } from "@/components/FacilitySlider";
import { Testimonials } from "@/components/Testimonials";
import { HelpCTA } from "@/components/HelpCTA";
import { SITE, HERO, INTRO, PROGRAMS, FEATURES, PROCESS } from "@/data/content";
import { Seo } from "@/components/Seo";

const HeroBg = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden">
      <motion.img
        style={{ y, scale }}
        src="https://images.unsplash.com/photo-1771868536783-ce35d0da644c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
        alt="Sunrise over calm water"
        className="h-[120%] w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-cream via-cream/40 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-maroon-deep/40 to-transparent" />
    </div>
  );
};

const Home = () => {
  return (
    <div data-testid="home-page">
      <Seo
        title="Sober Life Foundation — Alcohol & Drug Rehab Centre, Ulhasnagar"
        description="A trusted de-addiction & rehabilitation centre by Balkan Ji Bari Trust, with separate wings for men and women. Recover. Rebuild. Rediscover life."
        path="/"
      />
      {/* HERO */}
      <section className="relative min-h-screen flex items-end pb-20 md:pb-28">
        <HeroBg />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="text-xs md:text-sm uppercase tracking-[0.28em] text-gold mb-5 drop-shadow"
          >
            {HERO.overline}
          </motion.p>
          <h1 className="font-heading text-white text-6xl md:text-8xl lg:text-9xl leading-[0.95] drop-shadow-lg">
            <MaskedLines lines={HERO.lines} delay={0.35} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-7 max-w-xl text-white/90 text-lg leading-relaxed drop-shadow"
          >
            {HERO.subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a href={`tel:${SITE.phoneHref}`} data-testid="hero-help-cta" className="inline-flex items-center justify-center gap-3 bg-sunrise text-white px-8 py-4 rounded-full font-medium hover:bg-ember transition-colors duration-300 shadow-xl shadow-orange-900/30">
              <Phone size={18} /> Get Help Now
            </a>
            <Link to="/about" data-testid="hero-about-cta" className="inline-flex items-center justify-center gap-2 bg-white/90 backdrop-blur text-maroon-deep px-8 py-4 rounded-full font-medium hover:bg-white transition-colors duration-300">
              Our Story <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 grid md:grid-cols-12 gap-12 md:gap-16 items-start">
        <div className="md:col-span-5 md:sticky md:top-28">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-teal mb-5">{INTRO.overline}</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-maroon-deep leading-[1.05]">{INTRO.heading}</h2>
          </Reveal>
        </div>
        <div className="md:col-span-7 space-y-6">
          {INTRO.body.map((p, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <p className="text-stone-600 text-lg leading-relaxed">{p}</p>
            </Reveal>
          ))}
          <Reveal delay={0.2}>
            <div className="pt-4 flex flex-wrap gap-6">
              {["Compassion", "Integrity", "Dignity", "Hope"].map((v) => (
                <span key={v} className="font-heading italic text-2xl text-sunrise">{v}.</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="bg-cream-200 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-teal mb-4">Rehabilitation Programs</p>
            <h2 className="font-heading text-4xl md:text-6xl text-maroon-deep mb-14 max-w-2xl leading-[1.05]">Separate, dedicated care for men and women</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8">
            {PROGRAMS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.12}>
                <div data-testid={`program-card-${i}`} className="group bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 h-full">
                  <div className="relative h-72 overflow-hidden">
                    <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                    <span className="absolute top-5 left-5 font-heading text-5xl text-white/90 drop-shadow">{p.tag}</span>
                  </div>
                  <div className="p-8">
                    <h3 className="font-heading text-3xl text-maroon-deep mb-3">{p.title}</h3>
                    <p className="text-stone-600 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* FACILITY SLIDER */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-teal mb-4">Inside Our Centre</p>
          <h2 className="font-heading text-4xl md:text-6xl text-maroon-deep mb-12 max-w-2xl leading-[1.05]">A safe, clean and caring space to heal</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <FacilitySlider />
        </Reveal>
      </section>

      {/* FEATURES + PROCESS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 grid md:grid-cols-12 gap-16">
        <div className="md:col-span-6">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-teal mb-5">Why Choose Us</p>
            <h2 className="font-heading text-4xl md:text-5xl text-maroon-deep mb-10 leading-[1.05]">Everything you need to heal, in one place</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {FEATURES.map((f, i) => (
              <Reveal key={f} delay={i * 0.04}>
                <div className="flex items-start gap-3">
                  <span className="mt-1 h-5 w-5 rounded-full bg-teal/10 text-teal flex items-center justify-center shrink-0"><Check size={12} /></span>
                  <span className="text-stone-700">{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="md:col-span-6">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-teal mb-5">The Recovery Process</p>
            <h2 className="font-heading text-4xl md:text-5xl text-maroon-deep mb-10 leading-[1.05]">A gentle, guided path forward</h2>
          </Reveal>
          <div className="relative pl-8 border-l border-stone-200 space-y-8">
            {PROCESS.map((step, i) => (
              <Reveal key={step} delay={i * 0.08}>
                <div className="relative">
                  <span className="absolute -left-[42px] top-0.5 h-5 w-5 rounded-full bg-sunrise ring-4 ring-cream" />
                  <span className="block text-xs text-sunrise tracking-widest mb-1">STEP {String(i + 1).padStart(2, "0")}</span>
                  <span className="font-heading text-2xl text-maroon-deep">{step}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      <HelpCTA />
    </div>
  );
};

export default Home;
